/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import {Device} from '@ion/core/src/types/entities';
import {useDeviceRecord} from './useDeviceRecord';
import {getVbusStatus} from './ViewDeviceEditorUtils';

export interface UseDeviceVbusStatusParams {
  deviceId?: number | string;
  initialDevice?: Device;
}

export type UseDeviceVbusStatusResult = {
  status: string;
  mode: string;
  hasStatus: boolean;
  isLoading: boolean;
  isError: boolean;
  refresh: () => void;
};

/**
 * The useDeviceVbusStatus hook derives the VBUS status and mode the Device
 * Details dialog displays from the shared device record, so the dialog no
 * longer depends on a parent grid having loaded optional columns.
 *
 * State tracked is the VBUS status string, the VBUS mode, and whether a status
 * is known at all; it changes whenever the shared device query resolves or is
 * refreshed. Permissions are deliberately not an input here; the hook answers
 * what the current values are, and the caller separately decides whether an
 * action is allowed.
 *
 * `initialDevice` is a placeholder only. It fills the gap while the shared
 * device query is in flight and is discarded the moment that query resolves.
 *
 * ```typescript
 * import {useDeviceVbusStatus} from './useDeviceVbusStatus';
 *  ...
 *  const vbus = useDeviceVbusStatus({deviceId, initialDevice: device});
 * ```
 *
 * @param deviceId      of the device to read VBUS data for
 * @param initialDevice already-loaded device used only while loading
 * @returns the VBUS status and mode, load state, and a refresh function
 */
export const useDeviceVbusStatus = ({
  deviceId,
  initialDevice
}: UseDeviceVbusStatusParams): UseDeviceVbusStatusResult => {
  const {
    device,
    isLoading,
    isError,
    refresh
  } = useDeviceRecord({deviceId, fallbacks: [initialDevice]});

  const status = getVbusStatus(device);

  return {
    mode      : device?.extensions?.vbusMode || '',
    hasStatus : !isEmpty(status),
    status,
    isLoading,
    isError,
    refresh
  };
};

export default useDeviceVbusStatus;
